import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ShoppingBag, Receipt, Calculator, ArrowDownToLine, CreditCard, Download, Smartphone, Activity, MoonStar, AlertTriangle } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../i18n/I18nProvider';
import { useSession } from '../../session/SessionProvider';
import { errorText } from '../../lib/errors';
import { PageHeader, StatCard, Card, CardHeader, CardBody, TrendChart, DataTable, Select, Button, EmptyState, LoadingBlock, Avatar } from '../../ui';
import EmployeeDrawer from '../employees/EmployeeDrawer';
import Heatmap from './Heatmap';

const PERIODS = [7, 30, 90, 365];
const growth = (curr, prev) => (prev > 0 ? Math.round(((curr - prev) / prev) * 100) : null);

/** Аналитика: кабинет компании (своя) и консоль владельца (все компании или выбранная) */
export default function AnalyticsPage() {
  const { t, fmt } = useI18n();
  const { isOwner, isViewingAsOwner } = useSession();
  const platformWide = isOwner && !isViewingAsOwner;
  const [params, setParams] = useSearchParams();
  const days = PERIODS.includes(Number(params.get('days'))) ? Number(params.get('days')) : 30;
  const companyId = platformWide ? params.get('company') || '' : '';
  const [companies, setCompanies] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [userId, setUserId] = useState(null);

  const headers = useMemo(() => (companyId ? { 'X-Admin-Tenant-ID': companyId } : {}), [companyId]);

  useEffect(() => {
    if (!platformWide) return;
    api.get('/admin/tenants').then((res) => setCompanies(res.data.data || [])).catch(() => {});
  }, [platformWide]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get('/admin/analytics/company', { params: { days }, headers })
      .then((res) => { if (alive) setData(res.data); })
      .catch((err) => toast.error(errorText(err, t('common.error'))))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days, headers, t]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get('/admin/analytics/export/transactions', { params: { days }, headers, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transactions_${days}d_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('analytics.exported'));
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setExporting(false);
    }
  };

  const cur = data?.current;
  const prev = data?.previous;
  const money = (v) => fmt.money(v);

  return (
    <>
      <PageHeader
        title={t('analytics.title')}
        description={platformWide ? t('analytics.subtitleOwner') : t('analytics.subtitleCompany')}
        actions={
          <>
            {platformWide && (
              <div className="w-48">
                <Select value={companyId} onChange={(e) => setParam('company', e.target.value)} aria-label={t('push.company')}>
                  <option value="">{t('analytics.allCompanies')}</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Select>
              </div>
            )}
            <div className="w-32">
              <Select value={days} onChange={(e) => setParam('days', e.target.value === '30' ? '' : e.target.value)} aria-label={t('analytics.period')}>
                {PERIODS.map((d) => <option key={d} value={d}>{t(`analytics.periods.${d}`)}</option>)}
              </Select>
            </div>
            <Button variant="secondary" icon={Download} onClick={exportCsv} loading={exporting}>{t('analytics.export')}</Button>
          </>
        }
      />

      {!data ? <LoadingBlock /> : (
        <div className={loading ? 'pointer-events-none opacity-60 transition-opacity' : 'transition-opacity'}>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard label={t('analytics.spend')} value={money(cur.spend)} delta={growth(cur.spend, prev.spend)} hint={growth(cur.spend, prev.spend) !== null ? t('analytics.vsPrev') : null} icon={ShoppingBag} tone="brand" />
            <StatCard label={t('analytics.checks')} value={fmt.number(cur.checks)} delta={growth(cur.checks, prev.checks)} hint={growth(cur.checks, prev.checks) !== null ? t('analytics.vsPrev') : null} icon={Receipt} />
            <StatCard label={t('analytics.avgCheck')} value={money(cur.avg_check)} delta={growth(cur.avg_check, prev.avg_check)} hint={growth(cur.avg_check, prev.avg_check) !== null ? t('analytics.vsPrev') : null} icon={Calculator} />
            <StatCard label={platformWide && !companyId ? t('overview.seriesTopup') : t('analytics.topup')} value={money(cur.topup)} delta={growth(cur.topup, prev.topup)} hint={growth(cur.topup, prev.topup) !== null ? t('analytics.vsPrev') : null} icon={ArrowDownToLine} />
          </div>

          <h2 className="mb-3 mt-8 text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('analytics.engagement')}</h2>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard label={t('analytics.spenders')} value={fmt.number(cur.spenders)} hint={t('analytics.spendersHint', { cards: fmt.number(data.cards.total) })} icon={CreditCard} />
            <StatCard label={t('analytics.inApp')} value={fmt.number(data.employees.registered)} hint={t('analytics.inAppHint', { total: fmt.number(data.employees.total) })} icon={Smartphone} />
            <StatCard label={t('analytics.idle')} value={fmt.number(data.cards.idle)} hint={t('analytics.idleHint')} icon={MoonStar} tone={data.cards.idle ? 'warning' : 'neutral'} />
            <StatCard
              label={t('analytics.negative')}
              value={fmt.number(data.cards.negative_count)}
              hint={data.cards.negative_count ? t('analytics.negativeHint', { sum: money(data.cards.negative_sum) }) : null}
              icon={data.cards.negative_count ? AlertTriangle : Activity}
              tone={data.cards.negative_count ? 'danger' : 'neutral'}
            />
          </div>

          <Card className="mt-6">
            <CardHeader title={t('analytics.trend')} description={t(`analytics.periods.${days}`)} />
            <CardBody>
              <TrendChart
                data={data.trend}
                days={days}
                series={[{ key: 'spend', label: t('analytics.spend') }, { key: 'topup', label: platformWide && !companyId ? t('overview.seriesTopup') : t('analytics.topup') }]}
                formatValue={(v) => fmt.number(v, { notation: 'compact', maximumFractionDigits: 1 })}
                formatDate={(d) => fmt.date(d)}
              />
            </CardBody>
          </Card>

          <div className="mt-6">
            <VenuesTable venues={data.venues} />
          </div>

          <Card className="mt-6">
            <CardHeader title={t('analytics.heatmap')} description={t('analytics.heatmapHint')} />
            <CardBody>
              {data.heatmap.length ? <Heatmap cells={data.heatmap} /> : <p className="text-sm text-zinc-500">{t('analytics.noData')}</p>}
            </CardBody>
          </Card>

          <div className="mt-6">
            <TopEmployees rows={data.top_employees} onOpen={setUserId} />
          </div>

          {data.companies && (
            <div className="mt-6">
              <CompaniesTable rows={data.companies} />
            </div>
          )}
        </div>
      )}

      <EmployeeDrawer userId={userId} onClose={() => setUserId(null)} onChanged={() => {}} />
    </>
  );
}

function ShareBar({ value, max }) {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
      <div className="h-full rounded-full bg-[#2a78d6] dark:bg-[#3987e5]" style={{ width: `${max ? Math.max(3, (value / max) * 100) : 0}%` }} />
    </div>
  );
}

function VenuesTable({ venues }) {
  const { t, fmt } = useI18n();
  const total = venues.reduce((s, v) => s + (v.sum || 0), 0);
  const max = venues[0]?.sum || 0;
  const columns = [
    {
      key: 'name',
      header: t('analytics.venue'),
      sortable: true,
      render: (v) => (
        <div className="min-w-[10rem]">
          <p className="truncate font-medium text-zinc-900 dark:text-zinc-100">{v.name}</p>
          <div className="mt-1.5"><ShareBar value={v.sum} max={max} /></div>
        </div>
      ),
    },
    { key: 'sum', header: t('analytics.sum'), sortable: true, align: 'right', render: (v) => <span className="tabular">{fmt.money(v.sum)}</span> },
    { key: 'share', header: t('analytics.share'), align: 'right', sortValue: (v) => v.sum, hideOnMobile: true, render: (v) => <span className="tabular text-zinc-500">{total ? Math.round((v.sum / total) * 100) : 0}%</span> },
    { key: 'visits', header: t('analytics.visits'), sortable: true, align: 'right', render: (v) => <span className="tabular">{fmt.number(v.visits)}</span> },
    { key: 'avg', header: t('analytics.avgCheck'), sortable: true, align: 'right', hideOnMobile: true, render: (v) => <span className="tabular">{fmt.money(v.avg)}</span> },
    { key: 'spenders', header: t('analytics.people'), sortable: true, align: 'right', hideOnMobile: true, render: (v) => <span className="tabular">{fmt.number(v.spenders)}</span> },
  ];
  return (
    <div>
      <h2 className="mb-3 text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('analytics.venues')}</h2>
      <DataTable columns={columns} rows={venues} rowKey={(v) => v.name} empty={<EmptyState icon={ShoppingBag} title={t('analytics.noData')} />} />
    </div>
  );
}

function TopEmployees({ rows, onOpen }) {
  const { t, fmt } = useI18n();
  const columns = [
    {
      key: 'name',
      header: t('analytics.employee'),
      sortable: true,
      render: (r) => (
        <div className="flex items-center gap-3">
          <Avatar name={r.name || r.phone} size="sm" />
          <div className="min-w-0">
            <p className="truncate font-medium text-zinc-900 dark:text-zinc-100">{r.name || t('analytics.unknown')}</p>
            {r.phone && <p className="tabular truncate text-xs text-zinc-500">{r.phone}</p>}
          </div>
        </div>
      ),
    },
    { key: 'sum', header: t('analytics.sum'), sortable: true, align: 'right', render: (r) => <span className="tabular">{fmt.money(r.sum)}</span> },
    { key: 'visits', header: t('analytics.visits'), sortable: true, align: 'right', render: (r) => <span className="tabular">{fmt.number(r.visits)}</span> },
    { key: 'balance', header: t('employees.balance'), sortable: true, align: 'right', hideOnMobile: true, render: (r) => <span className={`tabular ${r.balance < 0 ? 'text-red-600 dark:text-red-400' : ''}`}>{r.balance === null ? '—' : fmt.money(r.balance)}</span> },
    { key: 'last', header: t('analytics.lastVisit'), sortable: true, align: 'right', hideOnMobile: true, render: (r) => <span className="text-zinc-500">{fmt.posRelative(r.last)}</span> },
  ];
  return (
    <div>
      <h2 className="mb-3 text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('analytics.topEmployees')}</h2>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.card_id}
        onRowClick={(r) => r.user_id && onOpen(Number(r.user_id))}
        empty={<EmptyState icon={ShoppingBag} title={t('analytics.noData')} />}
      />
    </div>
  );
}

function CompaniesTable({ rows }) {
  const { t, fmt } = useI18n();
  const max = Math.max(0, ...rows.map((r) => r.spend));
  const columns = [
    {
      key: 'name',
      header: t('employees.company'),
      sortable: true,
      render: (r) => (
        <div className="min-w-[10rem]">
          <p className="truncate font-medium text-zinc-900 dark:text-zinc-100">{r.name}</p>
          <div className="mt-1.5"><ShareBar value={r.spend} max={max} /></div>
        </div>
      ),
    },
    { key: 'spend', header: t('overview.seriesSpend'), sortable: true, align: 'right', render: (r) => <span className="tabular">{fmt.money(r.spend)}</span> },
    { key: 'topup', header: t('overview.seriesTopup'), sortable: true, align: 'right', render: (r) => <span className="tabular">{fmt.money(r.topup)}</span> },
    { key: 'spenders', header: t('analytics.people'), sortable: true, align: 'right', hideOnMobile: true, render: (r) => <span className="tabular">{fmt.number(r.spenders)}</span> },
  ];
  return (
    <div>
      <h2 className="mb-3 text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('analytics.byCompany')}</h2>
      <DataTable columns={columns} rows={rows} />
    </div>
  );
}
